/**
 * EditableWrapper - Scoped editing session for NeoNode instances
 *
 * Key features:
 * - Captures GraphStore baseline snapshot on creation
 * - Tracks scalar property changes via Proxy
 * - Tracks relationship changes via EditableRelationshipManager
 * - Filters all operations by ConstructionSpec
 * - Diffs against GraphStore baseline when building PATCH
 * - Scoped lifetime: create → edit → commit/discard
 *
 * Usage:
 *   const editable = thread.makeEditable();
 *   editable.name = "New Name";
 *   editable.steps.add(newStep);
 *   await editable.commit();  // or editable.discard()
 */

import { assertWritesAllowed } from './write-gate';
import { NeoNodeBase } from './neo-node-base';
import { GraphStore, graphStore, graphStoreJotaiStore } from './graph-store';
import { getFrameworkResolvers, type ConstructionSpec } from './resolvers';
import { EditableRelationshipManager } from './editable-relationship-manager';
import { atom, useAtomValue } from 'jotai';

// Track which models we've already warned about missing ConstructionSpec (warn once per model)
const warnedModels = new Set<string>();

/**
 * A save about to be sent: which node, and which of its scalar fields it
 * changes. A listener may return a function that is told how the save
 * ended (`true` once the server confirmed it, `false` when it failed).
 */
export interface SaveEvent {
  nodeId: string;
  modelName: string;
  fields: string[];
}
export type SaveListener = (event: SaveEvent) => ((ok: boolean) => void) | void;

const saveListeners = new Set<SaveListener>();

/** Hear about every save before its PATCH goes out (e.g. to show what it
 *  will make stale while it is in flight). Returns the unsubscribe. */
export function onSave(listener: SaveListener): () => void {
  saveListeners.add(listener);
  return () => { saveListeners.delete(listener); };
}

function announceSave(event: SaveEvent): (ok: boolean) => void {
  const outcomes: Array<(ok: boolean) => void> = [];
  saveListeners.forEach(listener => {
    try {
      const outcome = listener(event);
      if (outcome) outcomes.push(outcome);
    } catch (error) {
      console.error('Error in save listener:', error);
    }
  });
  return (ok: boolean) => outcomes.forEach(outcome => {
    try { outcome(ok); } catch (error) { console.error('Error in save listener:', error); }
  });
}

/**
 * Baseline snapshot captured from GraphStore
 * This represents the "before" state for diffing
 */
interface BaselineSnapshot {
  // Scalar properties from GraphStore node data
  properties: Record<string, any>;

  // Relationship data: propertyName → target node IDs
  relationships: Record<string, string[]>;
}

/**
 * EditableWrapper provides scoped editing session with change tracking
 */
export class EditableWrapper<T extends NeoNodeBase<any, any>> {
  private original: T;
  private baseline: BaselineSnapshot;
  private constructionSpec: ConstructionSpec | undefined;
  private graphStoreInstance: GraphStore;

  // Track scalar property changes
  private _optimisticProperties: Record<string, any> = {};

  // Track relationship managers (created lazily)
  private _relationshipManagers: Map<string, EditableRelationshipManager<any>> = new Map();

  // Atom holding optimistic properties (triggers re-renders when set)
  private _optimisticPropertiesAtom = atom<Record<string, any>>({});

  // Derived atom that triggers re-renders (legacy - for backward compat with Proxy pattern)
  // Re-computes when either the original node or optimistic properties change
  private _derivedAtom = atom((get) => {
    const nodeId = (this.original as any).id;
    if (nodeId) {
      // Subscribe to GraphStore node atom (for confirmed changes)
      get(this.graphStoreInstance.getNodeAtom(nodeId));
    }
    // Subscribe to optimistic properties (for pending changes)
    // Return the object itself - Jotai uses referential equality,
    // so creating a new object on each set will trigger re-renders
    return get(this._optimisticPropertiesAtom);
  });

  // Merged value atom - returns plain object combining GraphStore + optimistic
  // This is the proper Jotai pattern, similar to useNodes()
  private _mergedValueAtom = atom((get) => {
    const nodeId = (this.original as any).id;

    // Get base node from GraphStore
    const baseNode = nodeId ? get(this.graphStoreInstance.getNodeAtom(nodeId)) : this.original;

    // Get optimistic properties
    const optimisticProps = get(this._optimisticPropertiesAtom);

    // Merge and return plain object
    // Create new object reference so Jotai detects changes
    return { ...baseNode, ...optimisticProps } as T;
  });

  constructor(node: T, graphStoreInstance: GraphStore = graphStore, nestedSpec?: ConstructionSpec) {
    this.original = node;
    this.graphStoreInstance = graphStoreInstance;

    // Capture baseline from GraphStore (the "before" state)
    this.baseline = this.captureBaseline(node);

    // Get ConstructionSpec for filtering operations
    // nestedSpec overrides when created from a parent relationship's spec (e.g. via getEditable)
    const modelName = (node as any).modelName;
    this.constructionSpec = nestedSpec ?? getFrameworkResolvers().getModifyConstructionSpec(modelName);

    if (!this.constructionSpec && !nestedSpec && modelName && !warnedModels.has(modelName)) {
      warnedModels.add(modelName);
      // Suppress warning - this is expected for models without modify specs
    }

    // Return Proxy for transparent property access and mutation tracking
    return new Proxy(this, {
      get: (target, prop: string | symbol) => {
        // Handle symbol properties
        if (typeof prop === 'symbol') {
          return (target as any)[prop];
        }

        // First check if this is a method on EditableWrapper itself
        // This allows access to buildPatch, commit, discard, getBaseline, etc.
        if (prop in target && typeof (target as any)[prop] === 'function') {
          return (target as any)[prop].bind(target);
        }

        // Check if this is a property on EditableWrapper (e.g., _optimisticProperties)
        if (prop in target && !prop.startsWith('original') && !prop.startsWith('baseline')) {
          return (target as any)[prop];
        }

        // Check if this is a relationship property
        const relationshipProps = target.original.getRelationshipProperties();
        if (relationshipProps.includes(prop)) {
          return target.getRelationshipManager(prop);
        }

        // Return optimistic value if changed
        if (prop in target._optimisticProperties) {
          return target._optimisticProperties[prop];
        }

        // Read from current GraphStore state (not stale original instance)
        // This ensures we see server updates from DAG patches
        const nodeId = (target.original as any).id;
        const currentNode = nodeId ? target.graphStoreInstance.getNode(nodeId) : target.original;

        // If node was deleted, return undefined
        if (!currentNode) {
          return undefined;
        }

        const value = (currentNode as any)[prop];

        // Bind methods to original object
        if (typeof value === 'function') {
          return value.bind(target.original);
        }

        return value;
      },

      set: (target, prop: string | symbol, value: any) => {
        // Handle symbol properties
        if (typeof prop === 'symbol') {
          (target as any)[prop] = value;
          return true;
        }

        // Don't allow setting private properties
        if (prop.startsWith('_')) {
          throw new Error(`Cannot set private property '${prop}' on editable wrapper`);
        }

        // Check if this is a relationship property - not allowed via direct assignment
        const relationshipProps = target.original.getRelationshipProperties();
        if (relationshipProps.includes(prop)) {
          throw new Error(
            `Cannot directly assign to relationship property '${prop}'. ` +
            `Use .${prop}.add() / .${prop}.remove() / .${prop}.set() instead.`
          );
        }

        // Track scalar property change
        target._optimisticProperties[prop] = value;

        // Update atom with new optimistic properties to trigger reactive updates
        graphStoreJotaiStore.set(target._optimisticPropertiesAtom, { ...target._optimisticProperties });

        return true;
      },

      has: (target, prop: string | symbol) => {
        if (typeof prop === 'symbol') {
          return prop in target;
        }
        return prop in target._optimisticProperties || prop in target.original;
      },
    }) as any;
  }

  /**
   * Capture baseline state from GraphStore
   * This is the "before" state we'll diff against
   * TODO: Should we check if this changed before sending a patch?
   * We could a) diff from the current state or b) reject changes if the baseline changed?
   */
  private captureBaseline(node: T): BaselineSnapshot {
    const nodeId = (node as any).id;

    if (!nodeId) {
      throw new Error('Cannot create editable wrapper for node without ID');
    }

    // Get scalar properties from GraphStore node
    const storedNode = this.graphStoreInstance.getNode(nodeId);
    const properties: Record<string, any> = {};

    if (storedNode) {
      // Copy all scalar properties from the stored node
      // Exclude relationship properties and internal properties
      const relationshipProps = new Set(node.getRelationshipProperties());

      for (const key in storedNode) {
        if (
          !key.startsWith('_') &&
          !relationshipProps.has(key) &&
          typeof (storedNode as any)[key] !== 'function'
        ) {
          properties[key] = (storedNode as any)[key];
        }
      }
    }

    // Capture relationship state from GraphStore
    const relationships: Record<string, string[]> = {};
    const relationshipProps = this.graphStoreInstance.getRelationshipProperties(nodeId);

    for (const propName of relationshipProps) {
      const targetNodes = this.graphStoreInstance.getTargetNodes(nodeId, propName);
      relationships[propName] = targetNodes.map(n => (n as any).id);
    }

    return { properties, relationships };
  }

  /**
   * Get or create EditableRelationshipManager for a relationship property
   * Checks ConstructionSpec to ensure relationship is allowed
   */
  private getRelationshipManager(propName: string): EditableRelationshipManager<any> {
    const { isRelationshipAllowed, getNestedSpec } = getFrameworkResolvers();
    // Check if relationship is allowed by ConstructionSpec
    if (!isRelationshipAllowed(this.constructionSpec, propName)) {
      throw new Error(
        `Relationship '${propName}' is not allowed by ConstructionSpec for ${(this.original as any).modelName}`
      );
    }

    // Return existing manager if already created
    if (this._relationshipManagers.has(propName)) {
      return this._relationshipManagers.get(propName)!;
    }

    // Create new EditableRelationshipManager
    const originalManager = (this.original as any)[propName];
    const nestedSpec = getNestedSpec(this.constructionSpec, propName);

    // Get current relationship IDs from GraphStore (not stale baseline)
    // This allows wrapper reuse across multiple edit/save cycles
    const nodeId = (this.original as any).id;
    let currentRelationshipIds: string[] = [];
    if (nodeId) {
      const targetNodes = this.graphStoreInstance.getTargetNodes(nodeId, propName);
      currentRelationshipIds = targetNodes.map(n => (n as any).id).filter(Boolean);
    } else {
      // Fallback to baseline for nodes without ID (unsaved)
      currentRelationshipIds = this.baseline.relationships[propName] || [];
    }

    const editableManager = new EditableRelationshipManager(
      originalManager,
      currentRelationshipIds,
      nestedSpec,
      this.graphStoreInstance
    );

    this._relationshipManagers.set(propName, editableManager);
    return editableManager;
  }

  /**
   * Build PATCH by diffing current state against GraphStore
   * Only includes actual changes, filtered by ConstructionSpec
   *
   * Note: We diff against current GraphStore state, not the captured baseline,
   * to support reusable wrappers across multiple edit/save cycles
   */
  buildPatch(specOverride?: ConstructionSpec): any {
    const patch: any = {};
    const nodeId = (this.original as any).id;
    // serialised under a PARENT's path (a task's editable modifying this
    // thread): each relationship is judged by the parent's slice for it
    const { getNestedSpec } = getFrameworkResolvers();

    // Get current state from GraphStore
    const currentNode = nodeId ? this.graphStoreInstance.getNode(nodeId) : null;

    // 1. Diff scalar properties against current GraphStore state
    for (const [key, value] of Object.entries(this._optimisticProperties)) {
      // Only include if value actually changed from current GraphStore state
      const currentValue = currentNode ? (currentNode as any)[key] : this.baseline.properties[key];
      if (currentValue !== value) {
        patch[key] = value;
      }
    }

    // 2. Diff relationship operations against baseline
    for (const [propName, manager] of Array.from(this._relationshipManagers)) {
      const relationshipPatch = manager.buildPatch(
        specOverride !== undefined ? getNestedSpec(specOverride, propName) : undefined,
      );

      // Only include if there are actual operations
      if (relationshipPatch && Object.keys(relationshipPatch).length > 0) {
        patch[propName] = relationshipPatch;
      }
    }

    return patch;
  }

  /**
   * Save changes: send PATCH to server and update original object
   * Can be called multiple times - wrapper is reusable for multiple edit/save cycles
   *
   * @returns uuid_map mapping _localUuid to server-assigned UUIDs (for optimistic UI reconciliation)
   */
  async save(): Promise<Record<string, string>> {
    const patch = this.buildPatch();

    // If no changes, nothing to do
    if (Object.keys(patch).length === 0) {
      console.log('No changes to commit');
      return {};
    }

    const originalId = (this.original as any).id;
    if (!originalId) {
      throw new Error('Cannot save editable wrapper: original instance has no ID');
    }

    const modelName = (this.original as any).modelName;
    if (!modelName) {
      throw new Error('Cannot save editable wrapper: original instance has no modelName');
    }

    // Get API client from NeoNodeBase
    const client = (this.original.constructor as any).apiClient;
    if (!client) {
      throw new Error('API client not configured. Call setApiClient() first.');
    }

    // writes paused (a page the server has not confirmed): nothing is sent
    assertWritesAllowed();
    const settled = announceSave({
      nodeId: originalId,
      modelName,
      fields: Object.keys(this._optimisticProperties).filter(key => key in patch),
    });
    let confirmed = false;

    // Send PATCH to backend API
    try {
      const response = await client.patch('/api/modify', {
        model_name: modelName,
        id: originalId,
        data: patch,
      });

      // Extract uuid_map from response (maps _localUuid → server UUID)
      const uuidMap: Record<string, string> = response?.uuid_map || {};

      // Only apply changes if server confirms modification
      if (response?.modified === true) {
        // Apply scalar property changes to original object
        Object.assign(this.original, this._optimisticProperties);

        // Apply relationship changes to original managers and clear pending ops
        for (const [propName, editableManager] of Array.from(this._relationshipManagers)) {
          editableManager.applyToOriginal();
          // Server confirmed: reflect set/remove/add into the GraphStore's
          // relationship atoms so useNode/useNodes update immediately —
          // models outside any DAG context get no patch to do it for them.
          editableManager.applyOpsToGraphStore(uuidMap);
          // CRITICAL: Clear pending ops with uuid_map to release buffered DAG patches
          editableManager.clearPendingOps(uuidMap);
        }

        // Update GraphStore with new state
        this.graphStoreInstance.updateNode(this.original);

        // Clear optimistic state after successful save
        this._optimisticProperties = {};
        this._relationshipManagers.clear();

        // Clear the atom to trigger reactive updates
        graphStoreJotaiStore.set(this._optimisticPropertiesAtom, {});

        confirmed = true;
        return uuidMap;
      } else {
        console.warn('Server did not confirm modification:', response);
        throw new Error('Server did not confirm modification');
      }
    } catch (error) {
      console.error('PATCH failed:', error);
      // On failure, keep optimistic changes so user can retry or reset
      throw error;
    } finally {
      settled(confirmed);
    }
  }

  /**
   * Clear pending ops on all relationship managers (used by parent editable
   * to propagate uuid resolution to nested editables created via getEditable)
   */
  clearAllPendingOps(uuidMap: Record<string, string> = {}): void {
    for (const [, manager] of Array.from(this._relationshipManagers)) {
      manager.clearPendingOps(uuidMap);
    }
    this._optimisticProperties = {};
    this._relationshipManagers.clear();
  }

  /**
   * Discard all optimistic changes without saving
   *
   * This clears all pending changes in this editing session:
   * - Scalar property modifications
   * - Relationship operations (add/remove/set/modify)
   *
   * The original object and GraphStore remain unchanged.
   *
   * Use this to:
   * - Cancel an editing session
   * - Rollback after a failed save
   * - Clear changes before retrying
   *
   * @example
   * ```typescript
   * const editable = thread.makeEditable();
   * editable.name = "New Name";
   * editable.steps.add(step);
   *
   * // User cancels
   * editable.discard();  // All changes discarded
   * ```
   */
  discard(): void {
    // Clear all tracked changes
    this._optimisticProperties = {};
    this._relationshipManagers.clear();

    // Clear the atom to trigger reactive updates
    graphStoreJotaiStore.set(this._optimisticPropertiesAtom, {});
  }

  /**
   * Get the original (unedited) node instance
   */
  getOriginal(): T {
    return this.original;
  }

  /**
   * Get the baseline snapshot captured from GraphStore
   * Useful for debugging and understanding what changed
   */
  getBaseline(): BaselineSnapshot {
    return this.baseline;
  }

  /**
   * Get the ConstructionSpec being used for filtering
   */
  getConstructionSpec(): ConstructionSpec | undefined {
    return this.constructionSpec;
  }

  /**
   * Get the derived atom for reactive updates (legacy - for Proxy pattern)
   * Use with useAtomValue to trigger re-renders when properties change
   *
   * The derived atom subscribes to:
   * - GraphStore node atom (confirmed server changes)
   * - Optimistic properties atom (pending local changes)
   *
   * This ensures components re-render for both optimistic updates and server confirmations.
   *
   * @deprecated Use getMergedValueAtom() with useEditable hook instead
   *
   * @example
   * ```tsx
   * const editable = product.makeEditable();
   * const changeCount = useAtomValue(editable.getVersionAtom());
   * // Component re-renders when editable properties change
   * return <div>{editable.name}</div>
   * ```
   */
  getVersionAtom() {
    return this._derivedAtom;
  }

  /**
   * Get the merged value atom (GraphStore + optimistic properties)
   * This atom is meant to be used with useAtomValue in hooks, not called directly
   *
   * @returns Atom that provides merged object
   */
  getMergedValueAtom() {
    return this._mergedValueAtom;
  }

  /**
   * React hook that returns merged object (GraphStore + optimistic properties)
   * This is the proper Jotai pattern, similar to useNodes() for relationships
   *
   * @returns Merged object with confirmed values + optimistic changes
   *
   * @example
   * ```tsx
   * function ProductView({ product }: { product: PDMProduct }) {
   *   const editable = product.makeEditable();
   *   const current = editable.useValue(); // Merged object
   *
   *   const handleChange = (stage: string) => {
   *     editable.set('development_stage', stage);
   *     await editable.save();
   *   };
   *
   *   // Read from merged object (not Proxy)
   *   return <div>{current.name} - {current.development_stage}</div>;
   * }
   * ```
   */
  useValue(): T {
    // Subscribe to merged value atom
    // IMPORTANT: Use graphStoreJotaiStore instead of default React store
    const value = useAtomValue(this._mergedValueAtom, { store: graphStoreJotaiStore });
    return value;
  }

  /**
   * Set a scalar property value (optimistic update)
   * Triggers reactive updates for components using .useValue()
   *
   * @param prop Property name
   * @param value New value
   *
   * @example
   * ```tsx
   * editable.set('development_stage', 'prototype');
   * await editable.save();
   * ```
   */
  set(prop: string, value: any): void {
    // Validate property is not a relationship
    const relationshipProps = this.original.getRelationshipProperties();
    if (relationshipProps.includes(prop)) {
      throw new Error(
        `Cannot directly set relationship property '${prop}'. ` +
        `Use .${prop}.add() / .${prop}.remove() / .${prop}.set() instead.`
      );
    }

    // Track scalar property change
    this._optimisticProperties[prop] = value;

    // Update atom with new optimistic properties to trigger reactive updates
    graphStoreJotaiStore.set(this._optimisticPropertiesAtom, { ...this._optimisticProperties });
  }
}
